import { Component, inject, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { catchError, combineLatest, EMPTY, filter, map, Observable } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ModalButtons } from '@fcom/ui-components';
import { asPaxTypeKey } from '@fcom/dapi/utils';
import { MonitoringLogger } from '@fcom/core/services';
import { Amount, PriceService } from '@fcom/dapi';
import {
  FinnairAmount,
  FinnairCharge,
  FinnairPassengerCode,
  FinnairPassengerItem,
  FinnairPrice,
  FinnairTotalPricesDetails,
} from '@fcom/dapi/api/models';
import { safeMap as safeMapObs } from '@fcom/rx';
import { isPresent } from '@fcom/core/utils';
import { hasUnitPrices } from '@fcom/common/utils';

import { getPassengerServices } from '../../utils/order.utils';

interface BreakdownModel extends FinnairPassengerItem {
  prices: FinnairPrice;
  services: PriceService[];
  total: FinnairAmount;
}

interface TaxModalModel {
  taxes: FinnairCharge[];
  ptc: string;
  total: Amount;
}

const hasPassengers = (passengers: FinnairPassengerItem[]): boolean => isPresent(passengers) && passengers.length > 0;

@Component({
  selector: 'fin-price-breakdown',
  templateUrl: './price-breakdown.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PriceBreakdownComponent implements OnInit {
  private monitoringLogger = inject(MonitoringLogger);

  public readonly ModalButtons = ModalButtons;
  public readonly asPaxTypeKey = asPaxTypeKey;
  public readonly FinnairPassengerCode = FinnairPassengerCode;
  public readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input()
  prices$: Observable<FinnairTotalPricesDetails>;
  @Input()
  passengers$: Observable<FinnairPassengerItem[]>;
  @Input()
  showBaseFare = true;
  @Input()
  showTotalTax = true;
  @Input()
  showPoints = false;
  @Input()
  expandOpen = false;

  breakdowns$: Observable<BreakdownModel[]>;
  totalPrice$: Observable<Amount>;
  taxModalOpen = false;
  taxModalData: TaxModalModel;

  ngOnInit(): void {
    this.breakdowns$ = combineLatest([this.passengers$, this.prices$]).pipe(
      filter(([passengers, prices]) => hasPassengers(passengers) && hasUnitPrices(prices)),
      map(([passengers, prices]) =>
        passengers
          .filter((passenger) => parseFloat(prices.total.totalPerPax?.[passenger.id]?.totalAmount.amount) > 0)
          .map((passenger: FinnairPassengerItem) => ({
            ...passenger,
            prices: prices.flight?.totalPerPax?.[passenger.id] || prices.services?.totalPerPax?.[passenger.id],
            services: getPassengerServices(prices.services?.totalPerCategory, passenger),
            total: prices.total.totalPerPax?.[passenger.id]?.totalAmount,
          }))
      ),
      catchError((e: unknown) => {
        this.monitoringLogger.error('Exception in handling prices of passengers', e);
        return EMPTY;
      })
    );

    this.totalPrice$ = this.prices$.pipe(safeMapObs((prices) => ({ ...prices.total?.total?.totalAmount })));
  }

  openTaxModal(event: Event, taxes: FinnairCharge[], ptc: string, total: Amount): void {
    event.stopPropagation();

    this.taxModalOpen = true;
    this.taxModalData = {
      taxes,
      ptc,
      total,
    };
  }

  closeTaxModal(): void {
    this.taxModalData = null;
  }
}
